import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { ProductDetailsModal } from "../../../../../../interfaces/ProductObject";
import ProductCardHelper from "../../../../../../services/helpers/ProductCardHelper";
import { DineInKitchenItem } from "./dineInDetailBuilder";
import { DINE_IN_REJECT_REASONS } from "./useDineInBoardActions";
import "./styles/DineInRejectReasonModal.css";

interface DineInRejectReasonModalProps {
  item: DineInKitchenItem;
  product?: ProductDetailsModal;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * Asks why an item is being turned down. The reason reaches the table, so it's
 * worth one tap of thought — but only one: the preset reasons cover almost
 * every real case and submit immediately, and the text box is there for the
 * ones they don't.
 *
 * Portalled for the same reason as the QR modal — this renders from inside the
 * business page's tab content, under a sticky bar with its own stacking
 * context, where an in-place overlay would be trapped beneath it.
 */
const DineInRejectReasonModal: React.FC<DineInRejectReasonModalProps> = ({
  item,
  product,
  busy,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState("");
  const title = product ? ProductCardHelper.retrieveProductCard(product).title : "this item";

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onCancel, busy]);

  return createPortal(
    <div
      className="dine-in-reject__overlay"
      onClick={() => !busy && onCancel()}
      role="presentation"
    >
      <div
        className="dine-in-reject"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Reject ${title}`}
      >
        <button
          type="button"
          className="dine-in-reject__close"
          onClick={onCancel}
          disabled={busy}
          aria-label="Cancel"
        >
          <X size={18} />
        </button>

        <h3 className="dine-in-reject__title">Reject {title}?</h3>
        <p className="dine-in-reject__sub">
          ×{item.quantity} · the table will see why
        </p>

        <div className="dine-in-reject__reasons">
          {DINE_IN_REJECT_REASONS.map((preset) => (
            <button
              key={preset}
              type="button"
              className="dine-in-reject__preset"
              onClick={() => onConfirm(preset)}
              disabled={busy}
            >
              {preset}
            </button>
          ))}
        </div>

        <label className="dine-in-reject__label" htmlFor="dine-in-reject-reason">
          Or write a reason
        </label>
        <textarea
          id="dine-in-reject-reason"
          className="dine-in-reject__input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          maxLength={140}
          placeholder="e.g. chef says 20 min wait"
          disabled={busy}
        />

        <div className="dine-in-reject__actions">
          <button
            type="button"
            className="dine-in-reject__btn"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="dine-in-reject__btn dine-in-reject__btn--danger"
            onClick={() => onConfirm(reason)}
            disabled={busy || reason.trim().length === 0}
          >
            {busy ? "Rejecting..." : "Reject"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DineInRejectReasonModal;
